import React, { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import { nanoid } from '@reduxjs/toolkit';

import {
  APPROVED_ENTITY_CONTAINER_ID,
  APPROVED_ENTITY_TOASTER_OPTIONS,
} from 'utils/toastUtils';
import {
  isAdminSelector,
  isAuthenticatedSelector,
} from 'redux/selectors/authenticationSelectors';
import BaseAlert from 'components/wrappers/BaseAlert';
import BaseToast from 'components/wrappers/BaseToast';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { closeAllApprovedEntityNotifications } from 'utils/notificationUtils';
import { getApprovedEntitiesNotifications } from 'redux/reducers/notificationSlice';
import EntityApprovedDelayedNotification from './EntityApprovedDelayedNotification';
import { BackofficeInstantNotificationsResponseModel } from 'api/backofficeNotificationApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

export default function ApprovedEntityPopupContainer() {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);

  useEffect(() => {
    return () => {
      closeAllApprovedEntityNotifications();
    };
  }, []);

  useEffect(() => {
    if (isAdmin || !isAuthenticated) return;

    loadApprovedEntitiesNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isAuthenticated]);

  const loadApprovedEntitiesNotifications = async () => {
    const result = await dispatch(getApprovedEntitiesNotifications());

    if (getApprovedEntitiesNotifications.fulfilled.match(result)) {
      if (!result.payload) return;

      const { assignmentNotifications, customerNotifications } =
        result.payload as BackofficeInstantNotificationsResponseModel;

      if (!assignmentNotifications?.length && !customerNotifications?.length)
        return;

      // should use custom id to have access to it in EntityApprovedDelayedNotification
      const toastId = nanoid();
      BaseToast(
        <EntityApprovedDelayedNotification
          toastId={toastId}
          approvedEntities={result.payload}
        />,
        {
          toastId,
          containerId: APPROVED_ENTITY_CONTAINER_ID,
        },
      );
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <ToastContainer
      enableMultiContainer
      {...APPROVED_ENTITY_TOASTER_OPTIONS}
      containerId={APPROVED_ENTITY_CONTAINER_ID}
    />
  );
}
